import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  DatePicker,
  Empty,
  Flex,
  Form,
  Input,
  message,
  Popconfirm,
  Popover,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  TbBriefcase,
  TbCalendarPlus,
  TbClock,
  TbCopy,
  TbExternalLink,
  TbFileAnalytics,
  TbGavel,
  TbLink,
  TbMail,
  TbPhoneCall,
  TbScale,
  TbShieldCheck,
  TbTargetArrow,
  TbTrash,
  TbUserPlus,
  TbUsers,
} from "react-icons/tb";
import type { IconType } from "react-icons";
import { ApiService } from "api/api.service";
import { formatDateTime, getErrorMessage } from "utils/functions";
import { TeamRole, TeamMember as TeamMemberDto, Meeting, MeetingType } from "dtos/application.dto";
import { UserSchemaDto } from "dtos/user.dto";
import { pickColorFromSeed } from "utils/colors";
import { OptionProps, SelectProps } from "antd/es/select";

export type AssignableUser = Pick<UserSchemaDto, "_id" | "name" | "email" | "role" | "phone">;
type PopulatedTeamMember = { user: AssignableUser; role: TeamRole };

type RoleMeta = {
  value: TeamRole;
  label: string;
  color: string;
  icon: IconType;
  multiHolder: boolean;
};

export const TEAM_ROLES: RoleMeta[] = [
  { value: TeamRole.OPENER, label: "Opener", color: "green", icon: TbPhoneCall, multiHolder: false },
  { value: TeamRole.NEGOTIATOR, label: "Negotiator", color: "purple", icon: TbScale, multiHolder: false },
  { value: TeamRole.CSM, label: "CSM", color: "blue", icon: TbUsers, multiHolder: false },
  { value: TeamRole.ACSM, label: "Secondary CSM", color: "geekblue", icon: TbUsers, multiHolder: false },
  { value: TeamRole.CLOSER, label: "Closer", color: "red", icon: TbTargetArrow, multiHolder: false },
  { value: TeamRole.LEGAL, label: "Legal", color: "orange", icon: TbGavel, multiHolder: true },
  { value: TeamRole.UNDERWRITER, label: "Underwriter", color: "magenta", icon: TbFileAnalytics, multiHolder: false },
  { value: TeamRole.PROTECTION, label: "Protections", color: "green", icon: TbShieldCheck, multiHolder: true },
];

const ROLE_META = new Map(TEAM_ROLES.map((r) => [r.value, r]));

interface TeamSectionProps {
  appKey: string | undefined;
  // members?: PopulatedTeamMember[];
  initialMeetings?: Meeting[];
  onMembersChange?: (members: TeamMemberDto[]) => void;
  onMeetingsChange?: (meetings: Meeting[]) => void;
}

interface AssignFormValues {
  userId: string;
  roles: TeamRole[];
}

interface MeetingFormValues {
  title?: string;
  type: MeetingType;
  link: string;
  scheduledAt: Dayjs;
}

const initialsOf = (name: string) =>
  name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

type GroupedMember = { user: AssignableUser; roles: TeamRole[] };

const groupRows = (rows: PopulatedTeamMember[]): GroupedMember[] => {
  const map = new Map<string, GroupedMember>();
  for (const row of rows) {
    const existing = map.get(row.user._id);
    if (existing) existing.roles.push(row.role);
    else map.set(row.user._id, { user: row.user, roles: [row.role] });
  }
  return [...map.values()];
};

const toDto = (rows: PopulatedTeamMember[]): TeamMemberDto[] => rows.map((r) => ({ user: r.user._id, role: r.role }));

const MeetingTypeOptions: Record<MeetingType, NonNullable<SelectProps["options"]>[number]> = {
  [MeetingType.ONBOARDING]: {
    label: "Onboarding",
    value: MeetingType.ONBOARDING,
  },
  [MeetingType.CARD_PROCESS]: {
    label: "Card Process",
    value: MeetingType.CARD_PROCESS,
  },
  [MeetingType.OTHERS]: {
    label: "Others",
    value: MeetingType.OTHERS,
  },
};

export function TeamSection({ appKey, initialMeetings = [], onMembersChange }: Readonly<TeamSectionProps>) {
  const membersQuery = ApiService.application.useGetTeamMembers(appKey);
  const updateTeamMembers = ApiService.application.useUpdateTeamMembers();
  const updateMeetings = ApiService.application.useUpdateMeetings();

  const serverMembers = useMemo<PopulatedTeamMember[]>(() => membersQuery.data ?? [], [membersQuery.data]);

  const [rows, setRows] = useState<PopulatedTeamMember[]>(serverMembers);
  useEffect(() => {
    setRows(serverMembers);
  }, [serverMembers]);

  const [meetings, setMeetings] = useState<Meeting[]>(initialMeetings);

  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [assignForm] = Form.useForm<AssignFormValues>();
  const watchedUserId = Form.useWatch("userId", assignForm);

  const [userSearch, setUserSearch] = useState("");
  const usersQuery = ApiService.user.useGetPaginated({ page: 1, limit: 20, search: userSearch, role: [] });
  const userResults = usersQuery.data?.result.data ?? [];

  const [meetingPopoverOpen, setMeetingPopoverOpen] = useState(false);
  const [assignPopoverOpen, setAssignPopoverOpen] = useState(false);
  const [meetingForm] = Form.useForm<MeetingFormValues>();

  const grouped = useMemo(() => groupRows(rows), [rows]);
  const assignedUserIds = useMemo(() => new Set(rows.map((r) => r.user._id)), [rows]);

  const persistMembers = (nextRows: PopulatedTeamMember[], prevRows: PopulatedTeamMember[], successMessage: string) => {
    if (!appKey) {
      message.error("Application key is missing.");
      return;
    }
    setRows(nextRows);
    const dto = toDto(nextRows);
    updateTeamMembers.mutate(
      { appKey, members: dto },
      {
        onSuccess: () => {
          message.success(successMessage);
          onMembersChange?.(dto);
        },
        onError: (error) => {
          setRows(prevRows);
          message.error(getErrorMessage(error) || "Failed to update the team.");
        },
      },
    );
  };

  const openAssignModal = (member?: GroupedMember) => {
    if (member) {
      setEditingUserId(member.user._id);
      assignForm.setFieldsValue({ userId: member.user._id, roles: member.roles });
    } else {
      setEditingUserId(null);
      assignForm.resetFields();
    }
    setAssignPopoverOpen(true);
  };

  const closeAssignModal = () => {
    setAssignPopoverOpen(false);
    setEditingUserId(null);
    setUserSearch("");
    assignForm.resetFields();
  };

  const roleOptionsFor = (currentUserId?: string) =>
    TEAM_ROLES.map((role) => {
      const holder = role.multiHolder
        ? undefined
        : rows.find((r) => r.role === role.value && r.user._id !== currentUserId)?.user;
      return {
        value: role.value,
        label: (
          <Space className={holder ? "opacity-70" : ""}>
            {<role.icon color={role.color} />}
            <Typography.Text>
              {role.label}
              {holder?.name ? <span className="text-xs block opacity-70">held by {holder?.name}</span> : null}
            </Typography.Text>
          </Space>
        ),
        disabled: Boolean(holder),
      };
    });

  const handleAssign = (values: AssignFormValues) => {
    const user: AssignableUser | undefined = editingUserId
      ? rows.find((r) => r.user._id === editingUserId)?.user
      : userResults.find((u) => u._id === values.userId);

    if (!user) {
      message.error("Selected user not found.");
      return;
    }

    const conflict = values.roles.find((role) => {
      const meta = ROLE_META.get(role);
      return meta && !meta.multiHolder && rows.some((r) => r.role === role && r.user._id !== user._id);
    });
    if (conflict) {
      message.error(`${ROLE_META.get(conflict)?.label} is already assigned to another member.`);
      return;
    }

    const withoutUser = rows.filter((r) => r.user._id !== user._id);
    const nextRows = [...withoutUser, ...values.roles.map((role) => ({ user, role }))];
    const successMessage = editingUserId ? `Roles updated for ${user.name}.` : `${user.name} assigned.`;

    persistMembers(nextRows, rows, successMessage);
    closeAssignModal();
  };

  const removeMember = (member: GroupedMember) => {
    persistMembers(
      rows.filter((r) => r.user._id !== member.user._id),
      rows,
      `${member.user.name} removed from the team.`,
    );
  };

  const commitMeetings = (next: Meeting[], onSuccess: () => unknown) => {
    if (!appKey) return message.warning("AppKey not found!");
    const sorted = [...next].sort((a, b) => dayjs(a.time).valueOf() - dayjs(b.time).valueOf());
    updateMeetings.mutate(
      { appKey, meetings: sorted },
      {
        onSuccess: () => {
          setMeetings(sorted);
          onSuccess();
        },
      },
    );
  };

  const handleAddMeeting = (values: MeetingFormValues) => {
    commitMeetings(
      [
        ...meetings,
        {
          title: values.title?.trim(),
          type: values.type,
          link: values.link.trim(),
          time: values.scheduledAt.toISOString(),
        },
      ],
      () => {
        meetingForm.resetFields();
        setMeetingPopoverOpen(false);
        message.success(`Meeting scheduled on ${values.scheduledAt.format("lll")}.`);
      },
    );
  };

  const removeMeeting = (meeting: Meeting) => {
    commitMeetings(
      meetings.filter((m) => m.link !== meeting.link),
      () => message.success("Meeting cancelled and removed."),
    );
  };

  const userOptions = useMemo(() => {
    const base = userResults
      .filter((u) => !assignedUserIds.has(u._id))
      .map((u) => ({ value: u._id, label: `${u.name} <${u.email}>` }));
    if (editingUserId) {
      const editingUser = rows.find((r) => r.user._id === editingUserId)?.user;
      if (editingUser && !base.some((o) => o.value === editingUser._id)) {
        base.unshift({ value: editingUser._id, label: `${editingUser.name} <${editingUser.email}>` });
      }
    }
    return base;
  }, [userResults, assignedUserIds, editingUserId, rows]);

  const assignFormContent = (
    <Form<AssignFormValues> form={assignForm} layout="vertical" onFinish={handleAssign} className="mt-4 w-80">
      <Form.Item name="userId" label="User" rules={[{ required: true, message: "Select a user" }]}>
        <Select
          showSearch
          filterOption={false}
          onSearch={setUserSearch}
          loading={usersQuery.isFetching}
          notFoundContent={usersQuery.isFetching ? <Spin size="small" /> : null}
          placeholder="Search by name or email"
          disabled={Boolean(editingUserId)}
          options={userOptions}
        />
      </Form.Item>
      <Form.Item name="roles" label="Roles" rules={[{ required: true, message: "Assign at least one role" }]}>
        <Select
          mode="multiple"
          optionFilterProp="label"
          placeholder="Assign one or more roles"
          options={roleOptionsFor(editingUserId ?? watchedUserId)}
        />
      </Form.Item>
      <div className="flex justify-end gap-2">
        <Button size="small" onClick={() => setAssignPopoverOpen(false)}>
          Cancel
        </Button>
        <Button size="small" type="primary" onClick={() => assignForm.submit()}>
          Confirm
        </Button>
      </div>
    </Form>
  );

  const meetingFormContent = (
    <Form<MeetingFormValues>
      form={meetingForm}
      layout="vertical"
      onFinish={handleAddMeeting}
      className="grid w-[320px] grid-cols-2 gap-x-3"
    >
      <Form.Item
        className="col-span-2"
        name="link"
        label="Meeting link"
        rules={[
          { required: true, message: "Add a meeting link" },
          { type: "url", message: "Enter a valid URL" },
        ]}
      >
        <Input prefix={<TbLink className="text-gray-400" />} placeholder="https://meet.google.com/..." />
      </Form.Item>
      <Form.Item className="col-span-2" name="title" label="Title">
        <Input placeholder="Strategy review call" maxLength={120} />
      </Form.Item>
      <Form.Item
        className="col-span-1"
        name="scheduledAt"
        label="Date & time"
        rules={[{ required: true, message: "Pick a date and time" }]}
      >
        <DatePicker showTime format="MMM D, YYYY h:mm A" className="w-full h-9" />
      </Form.Item>
      <Form.Item
        className="col-span-1"
        name="type"
        label="Purpose"
        rules={[{ required: true, message: "Pick a purpose for this meeting" }]}
      >
        <Select options={Object.values(MeetingTypeOptions)} defaultValue={MeetingType.OTHERS} />
      </Form.Item>
      <div className="flex justify-end gap-2 col-span-2">
        <Button size="small" onClick={() => setMeetingPopoverOpen(false)}>
          Cancel
        </Button>
        <Button size="small" type="primary" onClick={() => meetingForm.submit()}>
          Confirm
        </Button>
      </div>
    </Form>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[2.5fr_1.5fr] gap-4 items-start">
      <div className="border border-solid border-gray-200 rounded-lg">
        <div className="flex items-center justify-between mx-3 my-3">
          <div className="flex items-center gap-2">
            <Typography.Title level={5} className="!m-0">
              Team Members
            </Typography.Title>
            <span className="text-sm font-medium py-0.5 px-2 rounded-2xl bg-gray-100">{grouped.length}</span>
          </div>
          <Popover
            trigger="click"
            placement="bottomRight"
            open={assignPopoverOpen}
            onOpenChange={(open) => {
              setAssignPopoverOpen(open);
              if (!open) assignForm.resetFields();
            }}
            content={assignFormContent}
            title="Add team members"
            destroyOnHidden
          >
            <Button
              icon={<TbUserPlus />}
              size="small"
              variant="outlined"
              color="purple"
              // onClick={() => openAssignModal()}
            >
              Assign Member
            </Button>
          </Popover>
          {/* <Button
            icon={<TbUserPlus />}
            size="small"
            variant="outlined"
            color="purple"
            onClick={() => openAssignModal()}
          >
            Assign Member
          </Button> */}
        </div>

        {grouped.length === 0 ? (
          <div className="py-12 flex justify-center">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No team members assigned yet" />
          </div>
        ) : (
          <div className="flex flex-col">
            {grouped.map((member) => (
              <div
                key={member.user._id}
                className="flex items-center gap-3 px-3 py-3 border-0 border-t border-solid border-gray-100"
              >
                <Avatar style={{ background: pickColorFromSeed(member.user.email) }} className="flex-shrink-0">
                  {initialsOf(member.user.name)}
                </Avatar>
                <div className="min-w-0 flex-1">
                  <Typography.Text className="font-semibold block truncate">{member.user.name}</Typography.Text>
                  <Flex gap={8}>
                    {member.user.email && (
                      <div className="inline-flex items-center gap-1 text-gray-400 text-xs max-w-full">
                        <TbMail size={13} className="flex-shrink-0" />
                        <span className="truncate">{member.user.email}</span>
                      </div>
                    )}
                    {member.user.phone?.number && (
                      <div className="inline-flex items-center gap-1 text-gray-400 text-xs max-w-full">
                        <TbPhoneCall size={13} className="flex-shrink-0" />
                        <span className="truncate">
                          +{member.user.phone.code ?? "1"} {member.user.phone?.number}
                        </span>
                      </div>
                    )}
                  </Flex>
                </div>
                <div className="flex flex-wrap gap-1 items-center justify-end max-w-[320px] self-end ml-auto">
                  {member.roles.map((role) => {
                    const meta = ROLE_META.get(role);
                    const Icon = meta?.icon;
                    return (
                      <Tag
                        key={role}
                        color={meta?.color}
                        icon={Icon ? <Icon size={12} className="inline align-[-2px] mr-0.5" /> : undefined}
                        className="!m-0"
                      >
                        {meta?.label ?? role}
                      </Tag>
                    );
                  })}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Tooltip title="Edit roles">
                    <Button type="text" size="small" icon={<TbBriefcase />} onClick={() => openAssignModal(member)} />
                  </Tooltip>
                  <Popconfirm
                    title="Remove from team?"
                    description={`Unassign ${member.user.name} from this application.`}
                    okText="Remove"
                    okButtonProps={{ danger: true, loading: updateTeamMembers.isPending }}
                    onConfirm={() => removeMember(member)}
                  >
                    <Button type="text" size="small" danger icon={<TbTrash />} />
                  </Popconfirm>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-solid border-gray-200 rounded-lg">
        <div className="flex items-center justify-between mx-3 my-3">
          <Typography.Title level={5} className="!m-0">
            Meetings
          </Typography.Title>
          <Popover
            trigger="click"
            placement="bottomRight"
            open={meetingPopoverOpen}
            onOpenChange={(open) => {
              setMeetingPopoverOpen(open);
              if (!open) meetingForm.resetFields();
            }}
            content={meetingFormContent}
            title="Schedule a meeting"
            destroyOnHidden
          >
            <Button icon={<TbCalendarPlus />} size="small" variant="outlined" color="purple">
              Schedule
            </Button>
          </Popover>
        </div>

        {meetings.length === 0 ? (
          <div className="py-10 flex justify-center">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No meetings scheduled" />
          </div>
        ) : (
          <div className="flex flex-col">
            {meetings.map((meeting) => {
              const isPast = dayjs(meeting.time).isBefore(dayjs());
              return (
                <div
                  key={meeting.link}
                  className={`px-3 py-3 border-0 border-t border-solid border-gray-100 ${isPast ? "opacity-50" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <Typography.Text
                      className="font-semibold leading-tight font-mono"
                      copyable={{ icon: <TbCopy />, onCopy: () => navigator.clipboard.writeText(meeting.link) }}
                    >
                      <span
                        className={`font-sans block text-xs opacity-80 line-clamp-1 ${meeting.title ? "" : "italic !opacity-60"}`}
                      >
                        {meeting.title || "No Title"}
                      </span>
                      {meeting.link.replace("http://", "").replace("https://", "")}
                    </Typography.Text>
                    <Popconfirm
                      title="Delete this meeting?"
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => removeMeeting(meeting)}
                    >
                      <Button type="text" size="small" danger icon={<TbTrash size={14} />} />
                    </Popconfirm>
                  </div>
                  <div className="inline-flex items-center gap-1 text-gray-400 text-xs mt-1">
                    <TbClock size={13} />
                    <span>{formatDateTime(meeting.time)}</span>
                  </div>
                  <div>
                    <a
                      href={meeting.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-purple-600 text-sm mt-1 no-underline hover:underline"
                    >
                      <TbExternalLink size={14} />
                      Join meeting
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* <Modal
        title={editingUserId ? "Edit assignment" : "Assign team member"}
        open={assignModalOpen}
        okText={editingUserId ? "Save" : "Assign"}
        onOk={() => assignForm.submit()}
        onCancel={closeAssignModal}
        okButtonProps={{ loading: updateTeamMembers.isPending }}
        destroyOnHidden
      >
        <Form<AssignFormValues> form={assignForm} layout="vertical" onFinish={handleAssign} className="mt-4">
          <Form.Item name="userId" label="User" rules={[{ required: true, message: "Select a user" }]}>
            <Select
              showSearch
              filterOption={false}
              onSearch={setUserSearch}
              loading={usersQuery.isFetching}
              notFoundContent={usersQuery.isFetching ? <Spin size="small" /> : null}
              placeholder="Search by name or email"
              disabled={Boolean(editingUserId)}
              options={userOptions}
            />
          </Form.Item>
          <Form.Item name="roles" label="Roles" rules={[{ required: true, message: "Assign at least one role" }]}>
            <Select
              mode="multiple"
              optionFilterProp="label"
              placeholder="Assign one or more roles"
              options={roleOptionsFor(editingUserId ?? watchedUserId)}
            />
          </Form.Item>
        </Form>
      </Modal> */}
    </div>
  );
}

export default TeamSection;
